import React, { useCallback, useEffect, useMemo, } from 'react';
import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import { AtlasForm, IForm, IFormOptions, } from 'atlas-form';
import {
  PhonenumberMonthlyCostEditForm,
  PhonenumberMonthlyCostEditInitialValues,
  PhonenumberMonthlyCostComponents,
  PhonenumberMonthlyCostAdditionalParams,
  PhonenumberMonthlyCostFormOptions,
  onPhonenumberMonthlyCostFormSaved,
  IPhonenumberMonthlyCostFormOptionsProps,
  AlertError,
} from '@Utils';
import { ISharedPhonenumberMonthlyCost, ISharedPatchPhonenumberMonthlyCost, PermissionEnum, CrudEnum, } from 'atlas-shared';
import { fetchPhonenumberMonthlyCost, patchPhonenumberMonthlyCost, getPhonenumberMonthlyCostUpdateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withEdit, IWithEditProps, } from '@Hocs/with.edit';
import { useAbilities, useAuth, usePhonenumberMonthlyCostParents, useOrganizations, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithEditProps<ISharedPhonenumberMonthlyCost> {}

const PhonenumberMonthlyCostFormEdit = ({ id, value, setValue, formSchema, schemaLoading, saving, save, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const abilities = useAbilities();
  const dispatch = useAppDispatch();
  const phonenumber_monthly_cost_parents = usePhonenumberMonthlyCostParents();
  const organizations = useOrganizations();

  useEffect(() => {
    fetchPhonenumberMonthlyCost(id).then(phonenumber_monthly_cost => setValue(phonenumber_monthly_cost));
  }, [id, setValue, ]);

  const param_props: IPhonenumberMonthlyCostFormOptionsProps = {
    t,
    is_new: false,
    abilities,
    phonenumber_monthly_cost_parents: phonenumber_monthly_cost_parents.phonenumber_monthly_cost_parents,
    organizations: organizations.organizations,
  };
  const formLayout: IForm | undefined = useMemo(() => value && PhonenumberMonthlyCostEditForm(value, param_props, t), [value, phonenumber_monthly_cost_parents, organizations, t, abilities, ]);
  const options: IFormOptions | undefined = useMemo(() => value && PhonenumberMonthlyCostFormOptions(value, param_props, t), [value, phonenumber_monthly_cost_parents, organizations, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedPatchPhonenumberMonthlyCost, form) => {
    onChange?.(values, changedValue, form);
  }, []);
  const onFinish = useCallback((values: ISharedPatchPhonenumberMonthlyCost) => value && save(() => patchPhonenumberMonthlyCost(value.id, values).then(
    res => onSaved ? onSaved(res, navigate) : onPhonenumberMonthlyCostFormSaved(t, 'edit', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [value, save, navigate, t, ]);
  
  if (schemaLoading)
    return <AppSpinner />;

  if (!value)
    return <>{t('ITEM_NOT_FOUND')}</>;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!phonenumber_monthly_cost_parents.loaded || !organizations.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !value || abilities.can(null, CrudEnum.Update, PermissionEnum.PhonenumberMonthlyCost, value)}
    >
      <AtlasForm<ISharedPatchPhonenumberMonthlyCost>
        dispatch={dispatch}
        formLayout={formLayout as IForm}
        form={formSchema}
        options={options}
        components={PhonenumberMonthlyCostComponents}
        additionalParams={PhonenumberMonthlyCostAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={PhonenumberMonthlyCostEditInitialValues(value)}
        saving={saving}
        title={title ? t(title) : `${t('EDIT')} ${t('PHONENUMBER_MONTHLY_COST')}`}
        entityId={value.id}
        entity={value}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const PhonenumberMonthlyCostFormEditView = React.memo(withEdit(PhonenumberMonthlyCostFormEdit, getPhonenumberMonthlyCostUpdateSchema));
